import HeaderLogo from '@/assets/img/logo_active@2x.png';
import Footer from '@/components/Footer';
import LinkSafe from '@/components/LinkSafe';
import GlobalStyle from '@/GlobalStyle';
import { EVENT_LIST_URI } from '@/helpers/BoardHelper';
import { breakpoint } from '@/helpers/BreakpointHelper';
import CookiesUtil from '@/services/CookiesUtil';
import axios from 'axios';
import { Link, PageProps } from 'gatsby';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

/**
 * 종료 캠페인 안내 페이지 (WEB-373)
 * - 종료된 캠페인 URL 이 404 일 때 back(EtcInterceptor·404.jsp) / front(404.tsx) 에서 이 페이지로 보낸다.
 * - 진행 중 캠페인 최대 3건은 런타임 API 로 받는다 (재빌드 없이 반영).
 */

interface Campaign {
  boardIndexNumber: string;
  subject: string;
  contents: string;
  imgAttGrpNoTn: string;
  referUrl: string;
}

// 종료 안내 페이지 경유 유입·전환 집계용 트랙코드 (현업 생성 값)
const CLOSED_CAMPAIGN_TRACKCODE = `endcampaign_official_hp_endcam_banner`;
const UNICEF_HOST = /(^|\.)unicef\.or\.kr$/;

// 사이트 내부(상대경로)·unicef.or.kr 링크에만 트랙코드를 붙인다. 기존 trackcode 는 교체, 외부 사이트는 그대로
// 상대경로는 절대주소로 돌려준다 : gatsby Link 는 Gatsby 페이지가 아닌 경로(webapp 캠페인)로 이동할 때 쿼리를 버린다
const withTrackcode = (url: string) => {
  const isRelative = url.startsWith(`/`) && !url.startsWith(`//`);
  if (!isRelative && !/^(https?:)?\/\//.test(url)) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (!isRelative && !UNICEF_HOST.test(parsed.hostname)) return url;
    parsed.searchParams.set(`trackcode`, CLOSED_CAMPAIGN_TRACKCODE);
    return parsed.toString();
  } catch {
    return url;
  }
};

const Wrapper = styled.div`
  position: relative;
  min-height: 100vh;
  background: #fff;
`;

const Content = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroSection = styled.div`
  text-align: center;
  padding-top: 40px;

  ${breakpoint(`mobile`)} {
    padding-top: 40px;
  }
`;

const Logo = styled.h1`
  display: block;
  width: 130px;
  margin: 0 auto 84px;

  ${breakpoint(`mobile`)} {
    width: 78px;
    margin: 0 auto 40px;
  }
`;

const LogoLink = styled((props) => <Link {...props} />)`
  display: block;
  width: 100%;
  padding-top: 53.85%;
  background: url(${HeaderLogo}) no-repeat center center;
  background-size: contain;
`;

const Title = styled.h2`
  font-family: 'Pretendard', sans-serif;
  font-weight: 700;
  font-size: 40px;
  line-height: 1.5;
  color: #2d2926;

  ${breakpoint(`mobile`)} {
    font-weight: 800;
    font-size: 24px;
    line-height: 1.42;
    letter-spacing: -0.8px;
  }
`;

const MobileBreak = styled.br`
  display: none;

  ${breakpoint(`mobile`)} {
    display: block;
  }
`;

const Subtitle = styled.p`
  font-family: 'Pretendard', sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 1.6;
  color: #2d2926;
  margin-top: 30px;

  ${breakpoint(`mobile`)} {
    font-size: 14px;
    line-height: 1.57;
    letter-spacing: -0.8px;
    margin-top: 20px;
  }
`;

const CampaignSection = styled.section`
  padding-top: 120px;
  padding-bottom: 140px; // Footer TOP 버튼(top: -120px)이 마지막 카드를 가리지 않도록

  ${breakpoint(`mobile`)} {
    padding-top: 60px;
    padding-bottom: 100px; // 모바일 TOP 버튼(top: -90px)이 마지막 카드를 가리지 않도록
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;

  ${breakpoint(`mobile`)} {
    margin-bottom: 24px;
  }
`;

const SectionTitle = styled.h3`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
  font-size: 32px;
  line-height: 74px;
  letter-spacing: -0.8px;
  color: #2d2926;

  ${breakpoint(`mobile`)} {
    font-size: 16px;
    line-height: 27px;
  }
`;

const MoreLink = styled((props) => <Link {...props} />)`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.8px;
  color: #2d2926;

  &::after {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%232D2926' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='5' y1='12' x2='19' y2='12'/%3E%3Cpolyline points='13 6 19 12 13 18'/%3E%3C/svg%3E")
      no-repeat center center;
    background-size: contain;
  }

  ${breakpoint(`mobile`)} {
    gap: 4px;
    font-size: 14px;
    line-height: 16px;

    &::after {
      width: 20px;
      height: 20px;
    }
  }
`;

/* ---------- 카드 (PC·MO 공통 : 라운드 + 음영) ---------- */

const Card = styled.div`
  width: 100%;
  border-radius: 12px;
  box-shadow: 4px 4px 16px 0 rgba(0, 0, 0, 0.12);
  overflow: hidden;
  background: #fff;

  > a {
    display: block;
  }

  ${breakpoint(`mobile`)} {
    max-width: 335px;
    margin: 0 auto;
  }
`;

const CardImage = styled.div<{ $bgUrl: string }>`
  width: 100%;
  height: 160px;
  background: ${({ $bgUrl }) =>
    $bgUrl ? `url(${$bgUrl}) no-repeat center center` : `#f8f9fb`};
  background-size: cover;

  ${breakpoint(`mobile`)} {
    height: 148px;
  }
`;

const CardBody = styled.div`
  padding: 20px;
`;

// 제목은 항상 2줄 높이를 차지해 카드 높이를 일정하게 맞춘다
const CardTitle = styled.h4`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 700;
  font-size: 22px;
  line-height: 31px;
  height: 62px;
  letter-spacing: -0.8px;
  color: #2d2926;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: keep-all;

  ${breakpoint(`mobile`)} {
    font-size: 16px;
    line-height: 26px;
    height: 52px;
  }
`;

// 부제목이 없어도 같은 높이의 영역을 유지한다
const CardSubtitle = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  height: 24px;
  letter-spacing: -0.8px;
  color: #2d2926;
  margin-top: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${breakpoint(`mobile`)} {
    font-size: 14px;
    line-height: 22px;
    height: 22px;
    margin-top: 8px;
  }
`;

const CardAction = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 16px;
  font-family: 'Pretendard', sans-serif;
  font-weight: 600;
  font-size: 20px;
  line-height: 23px;
  letter-spacing: -0.8px;
  color: #009ee2;

  &::after {
    content: '';
    display: inline-block;
    width: 8px;
    height: 14px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='14' viewBox='0 0 8 14' fill='none' stroke='%23009EE2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='1 1 7 7 1 13'/%3E%3C/svg%3E")
      no-repeat center center;
    background-size: contain;
  }

  ${breakpoint(`mobile`)} {
    gap: 8px;
    margin-top: 8px;
    font-family: 'Noto Sans KR', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 27px;
    color: #1cabe2;

    &::after {
      width: 6px;
      height: 11px;
    }
  }
`;

/* ---------- 목록 : PC 3열 / 태블릿 2열 / MO 세로 ---------- */

// minmax(0, 1fr) : 부제목(nowrap)이 긴 카드가 있어도 열 폭을 같게 유지
const CardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  ${breakpoint(`tablet`)} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  ${breakpoint(`mobile`)} {
    grid-template-columns: minmax(0, 1fr);
    gap: 24px;
  }
`;

const CampaignCard: React.FC<{ campaign: Campaign }> = ({ campaign }) => (
  <Card>
    <LinkSafe
      to={withTrackcode(
        campaign.referUrl || `${EVENT_LIST_URI}${campaign.boardIndexNumber}`,
      )}
    >
      <CardImage $bgUrl={campaign.imgAttGrpNoTn || ``} />
      <CardBody>
        <CardTitle>{campaign.subject}</CardTitle>
        <CardSubtitle>{campaign.contents || ``}</CardSubtitle>
        <CardAction>참여하기</CardAction>
      </CardBody>
    </LinkSafe>
  </Card>
);

const ClosedCampaign: React.FC<PageProps> = () => {
  const [isMount, setIsMount] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);

  useEffect(() => {
    setIsMount(true);
    // 종료 캠페인 진입 시 back 이 심은 이전 트랙코드 쿠키 제거 (이 페이지 트랙코드가 우선하도록)
    CookiesUtil.removeCookie(`eventTrackCd`);

    let active = true;
    const fetchCampaigns = async () => {
      try {
        // back 공통 응답 래퍼(ResponseControllerAdvice) → { resultCode, data: [...] }
        const { data } = await axios.get<{ data?: Campaign[] }>(
          `${process.env.BACKEND_URL}/unicef/api/fo/event/active-campaigns`,
        );
        if (active) {
          setCampaigns(Array.isArray(data?.data) ? data.data : []);
        }
      } catch (e) {
        // 조회 실패 시 캠페인 영역만 숨긴다
        // eslint-disable-next-line no-console
        console.error(`Failed to fetch active campaigns`, e);
      }
    };
    fetchCampaigns();
    return () => {
      active = false;
    };
  }, []);

  if (!isMount) {
    return null;
  }

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        <Content>
          <HeroSection>
            <Logo>
              <LogoLink to="/">
                <span className="for-a11y">유니세프</span>
              </LogoLink>
            </Logo>
            <Title>
              이 캠페인은
              <MobileBreak />
              {` `}종료되었습니다
            </Title>
            <Subtitle>
              보내주신 관심과 응원 덕분에 캠페인이 잘 마무리되었습니다.
              <br />
              여러분의 따뜻한 마음은 지금도 아이들 곁에 있습니다.
            </Subtitle>
          </HeroSection>

          {campaigns.length > 0 && (
            <CampaignSection>
              <SectionHeader>
                <SectionTitle>지금 함께할 수 있는 캠페인</SectionTitle>
                <MoreLink
                  to={`${EVENT_LIST_URI}?trackcode=${CLOSED_CAMPAIGN_TRACKCODE}`}
                >
                  더보기
                </MoreLink>
              </SectionHeader>

              <CardGrid>
                {campaigns.map((campaign) => (
                  <CampaignCard
                    key={campaign.boardIndexNumber}
                    campaign={campaign}
                  />
                ))}
              </CardGrid>
            </CampaignSection>
          )}
        </Content>
        <Footer />
      </Wrapper>
    </>
  );
};

export default ClosedCampaign;
